<template>
  <section class="w-full">
    <!-- =========================
         BLOQUE 1: PARTES DEL SISTEMA
         ========================= -->
    <div class="ac_wrap ac_wrap--cuadros">
      <!-- Título -->
      <header class="flex justify-center mb-[30px]">
        <div class="flex items-start">
          <img
            src="/img/higienizacion/vector_negro.png"
            alt=""
            width="20"
            height="26"
            class="mt-[5px] mr-[5px]"
            loading="lazy"
            decoding="async"
          />

          <h2 class="font-bold text-center leading-[1.15] ac_h2">
            Estas son las principales partes de este sistema<br />
            y la función de cada una de ellas:
          </h2>
        </div>
      </header>

      <!-- Filas (imagen + texto) -->
      <div class="flex flex-col align-items-center">
        <article
            v-for="item in partes"
            :key="item.key"
            class="grid grid-cols-1 md:grid-cols-2"
        >
            <!-- IMAGEN (si reverse_en_desktop = true, en desktop pasa a la derecha) -->
            <div
                class="ac_img"
                :class="[
                item.img_class,
                item.reverse_en_desktop ? 'order-1 md:order-2' : 'order-1 md:order-1'
                ]"
                :style="item.img_style"
                aria-hidden="true"
            />

            <!-- TEXTO (si reverse_en_desktop = true, en desktop pasa a la izquierda) -->
            <div
                class="ac_text"
                :class="item.reverse_en_desktop ? 'order-2 md:order-1' : 'order-2 md:order-2'"
            >
                <h3 class="font-bold leading-[1.15] text-[32px]">
                {{ item.titulo }}
                </h3>

                <p class="mt-[16px] text-[16px] leading-[1.6] text-[#333333]">
                {{ item.descripcion }}
                </p>
            </div>
        </article>

      </div>
    </div>

    <!-- =========================
         BLOQUE 2: CONSEJOS
         ========================= -->
    <div class="ac_wrap ac_wrap--consejos">
      <!-- Título -->
      <header class="flex justify-center mb-[30px]">
        <div class="flex items-start">
          <img
            src="/img/higienizacion/vector_negro.png"
            alt=""
            width="20"
            height="26"
            class="mt-[5px] mr-[5px]"
            loading="lazy"
            decoding="async"
          />

          <h2 class="font-bold text-center leading-[1.15] ac_h2">
            Recuerda estos consejos para cuidar<br />
            tu sistema de aire acondicionado:
          </h2>
        </div>
      </header>

      <!-- Lista consejos -->
      <div class="flex flex-col items-center">
        <div
          v-for="item in consejos"
          :key="item.key"
          class="flex items-center mb-[30px]"
        >
          <img
            :src="item.icono_src"
            alt=""
            class="block shrink-0"
            loading="lazy"
            decoding="async"
          />

          <!-- En Laravel esto era .text-cajas { margin-left:20px; margin-top:40px; } -->
          <div class="ml-[20px] mt-[40px]">
            <h3 class="font-bold text-[18px] leading-[1.2]">
              {{ item.titulo }}
            </h3>

            <p class="mt-[10px] text-[16px] leading-[1.6] text-[#333333] max-w-[900px]">
              {{ item.descripcion }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
/**
 * AireAcondicionadoLegacy.vue
 * - Conversión “1:1” desde la vista Laravel a Vue3/Nuxt3 con Tailwind
 * - Mantiene:
 *   - Paddings (70/190), mobile (20), rango 1200–1800 (70/130)
 *   - Imágenes como background (cover desktop, contain mobile)
 *   - Altura visual por padding vertical (120px)
 *   - Orden mobile (en las filas “reverse”, la imagen debe quedar arriba)
 */

type Parte = {
  key: string;
  titulo: string;
  descripcion: string;

  /**
   * Clase que aplica el background correspondiente.
   * Nota: usamos CSS scoped para mantener exactamente el comportamiento del legacy (cover/contain).
   */
  img_class: string;

  /**
   * Si en desktop el texto va a la izquierda y la imagen a la derecha.
   * En mobile igual queremos que la imagen aparezca primero (arriba), como en tu CSS.
   */
  reverse_en_desktop?: boolean;

  /**
   * Si necesitas ajustar fino sin tocar CSS global, puedes setear estilo inline.
   * (ej: background-position-x específico)
   */
  img_style?: Record<string, string>;
};

type Consejo = {
  key: string;
  icono_src: string;
  titulo: string;
  descripcion: string;
};

const partes: Parte[] = [
  {
    key: 'compresor',
    titulo: 'Compresor del aire acondicionado automotriz',
    descripcion:
      'La función del compresor es aspirar y comprimir el gas refrigerante proveniente del evaporador. Para hacerlo, el compresor incrementa la presión en el gas refrigerante, que continúa avanzando hasta llegar al condensador.',
    img_class: 'ac_img--1',
    reverse_en_desktop: false,
  },
  {
    key: 'condensador',
    titulo: 'Condensador del aire acondicionado automotriz',
    descripcion:
      'El gas que ha sido comprimido pasa a la parte superior del condensador, donde comienza a enfriarse hasta que se condensa y llega a la base de esta pieza en estado líquido. Mientras tiene lugar el proceso que acabamos de describir, el filtro deshidratador se encarga de absorber la humedad y retener las impurezas y partículas generadas dentro del aire acondicionado automotriz. Esto, con la finalidad de que los microorganismos no lleguen a la válvula de expansión ni al compresor del aire.',
    img_class: 'ac_img--2',
    reverse_en_desktop: true,
  },
  {
    key: 'valvula',
    titulo: 'Válvula de expansión del aire acondicionado',
    descripcion:
      'La válvula de expansión del aire acondicionado automotriz hace que la presión y la temperatura desciendan. Así, el líquido o refrigerante generado en el condensador se descomprime y se enfría mucho más. De esta manera, empiezan a condicionarse los refrigerantes líquidos que van a pasar desde el condensador hasta el evaporador.',
    img_class: 'ac_img--3',
    reverse_en_desktop: false,
  },
  {
    key: 'evaporador',
    titulo: 'Evaporador del aire acondicionado automotriz',
    descripcion:
      'El refrigerante frío y de presión baja que sale de la válvula de expansión llega al evaporador, donde se convierte en gas, se enfría aún más y es impulsado por un ventilador hacia el habitáculo. Así se genera el aire que logra refrescar el vehículo.',
    img_class: 'ac_img--4',
    reverse_en_desktop: true,
  },
  {
    key: 'filtro',
    titulo: 'Filtro habitáculo',
    descripcion:
      'El filtro del habitáculo se encarga de eliminar todas aquellas partículas externas que entran en el habitáculo del automóvil y que pueden contaminar el aire. Gracias a él, el vehículo queda libre de bacterias, hollín, polvo, polen, etc., garantizando la calidad del aire que circula dentro del vehículo.',
    img_class: 'ac_img--5',
    reverse_en_desktop: false,
  },
];

const consejos: Consejo[] = [
  {
    key: 'mantencion',
    icono_src: '/img/higienizacion/ico1.png',
    titulo: 'Realizar un correcto mantenimiento',
    descripcion:
      'El mantenimiento del aire acondicionado no es algo que deba preocuparte solo en verano, sino que debe realizarse a lo largo del año. Para ello deberías encenderlo, al menos, una vez al mes. Así evitarás que los conductos se resequen y que proliferen en su interior bacterias o moho.',
  },
  {
    key: 'olor',
    icono_src: '/img/higienizacion/ico2.png',
    titulo: 'El mal olor en el habitáculo significa que algo va mal.',
    descripcion:
      'Si por las rejillas del aire acondicionado te llega mal olor, quizás necesites cambiar el filtro. Pero también es posible que se deba a la proliferación de bacterias y hongos en los conductos del aire. Para evitarlo, debes realizar una correcta limpieza profesional.',
  },
  {
    key: 'uso',
    icono_src: '/img/higienizacion/ico3.png',
    titulo: 'Utiliza correctamente el aire acondicionado',
    descripcion:
      'No es adecuado que abuses del climatizador. A la hora de enfriar tu vehículo, sobre todo en los días calurosos, baja las ventanas y espera un par de minutos antes de encender el aire acondicionado. Una vez que el aire se haya renovado, puedes cerrarlas y conectar el climatizador.',
  },
  {
    key: 'maximo',
    icono_src: '/img/higienizacion/ico4.png',
    titulo: 'No lo pongas al máximo cuando recién enciendes el vehículo',
    descripcion:
      'No debes encender el aire acondicionado para enfriar el vehículo apenas lo pones en funcionamiento. Lo adecuado es enfriar el sistema a baja potencia y, una vez que notes que el aire deja de salir caliente, subir la intensidad de forma progresiva.',
  },
  {
    key: 'temperatura',
    icono_src: '/img/higienizacion/ico5.png',
    titulo: 'Controla la temperatura',
    descripcion:
      'Para evitar un innecesario gasto energético y cambios bruscos de temperatura, intenta mantener siempre constante la temperatura del vehículo (entre 22 y 24 ºC). Asimismo, si el aire acondicionado está en funcionamiento, evita abrir las ventanas y colocarte justo enfrente del caudal del mismo.',
  },
];
</script>

<style scoped>
/* =========================================================
   1) WRAPPERS: padding legacy exacto
   - desktop: 70px 190px
   - mobile <=800: 20px
   - 1200–1800: 70px 130px
   ========================================================= */
.ac_wrap {
  padding: 70px 190px;
}

@media (max-width: 800px) {
  .ac_wrap {
    padding: 20px;
  }
}

@media screen and (min-width: 1200px) and (max-width: 1800px) {
  .ac_wrap {
    padding: 70px 130px;
  }
}

/* Tipografía base de títulos h2 (en Laravel era h2 bold + en mobile font-size:25) */
.ac_h2 {
  font-size: 32px; /* similar al h2 “browser default” pero controlado */
}

@media (max-width: 800px) {
  .ac_h2 {
    font-size: 25px;
  }
}

/* =========================================================
   2) BLOQUE PARTES: imagen como background (legacy)
   - background-repeat: no-repeat
   - background-position: right
   - padding vertical para altura visual: 120px
   - desktop: cover
   - mobile: contain
   ========================================================= */
.ac_img {
  background-repeat: no-repeat;
  background-position: right;
  background-size: cover;
  padding: 140px 0px;
}

/* En mobile el legacy forzaba contain para todas las imágenes */
@media (max-width: 800px) {
  .ac_img {
    background-size: contain;
  }
}

/* Backgrounds (legacy: /img/higienizacion/img1.jpg ... img5.jpg) */
.ac_img--1 {
  background-image: url('/img/higienizacion/img1.jpg');
}
.ac_img--2 {
  background-image: url('/img/higienizacion/img2.jpg');
}
.ac_img--3 {
  background-image: url('/img/higienizacion/img3.jpg');
}
.ac_img--4 {
  background-image: url('/img/higienizacion/img4.jpg');
}
.ac_img--5 {
  background-image: url('/img/higienizacion/img5.jpg');
}

/* =========================================================
   3) Texto derecha: replicar .text-aire legacy
   - display:flex, column, center
   - padding-left: 50px, padding-top:20px
   - mobile: sin padding-left
   ========================================================= */
.ac_text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px 20px 20px 50px;
}

@media (max-width: 800px) {
  .ac_text {
    padding-left: 0px;
  }
}
</style>
